import React from 'react'
import { useTranslation } from 'react-i18next'

import { Section } from '@/components/business/market-shop/section'
import { Typography } from '@/components/ui/typography'
import { ArrayElement } from '@/models'
import { formatPrice } from '@/utils'

import * as S from './order-summary.styled'

export type OrderSummaryProps<T extends unknown[]> = {
  products: T
  transformProductItem: (
    element: ArrayElement<T>,
    index: number,
  ) => { label: string; quantity: number; price: number; note?: string }
  tipAmount: number
  subtotal: number
  discount: number
  tax: number
  total: number
  orderNote?: string
}

export const OrderSummary = <T extends unknown[]>({
  products,
  transformProductItem,
  tipAmount,
  subtotal,
  discount,
  tax,
  total,
  orderNote,
}: OrderSummaryProps<T>) => {
  const { t } = useTranslation(['market-shop'])

  return (
    <Section>
      <Typography as="h2" size="heading-medium" weight="semibold" color="black800">
        {t('market-shop:orderSummary.title')}
      </Typography>
      <S.OrderSummaryModuleContent>
        <S.ProductsWrapper>
          {products.map((product, index) => {
            const { label, quantity, price, note } = transformProductItem(
              product as ArrayElement<T>,
              index,
            )

            return (
              <S.ProductRow key={`${index}-${label}`}>
                <S.Texts>
                  <S.CartMainText forwardedAs="span" color="black800" breakLines={2}>
                    {t('market-shop:orderSummary.quantityLabel', { label, quantity })}
                  </S.CartMainText>
                  <S.CartMainText forwardedAs="span" color="black800" whiteSpace="nowrap">
                    {formatPrice(price, { zeroPrice: true })}
                  </S.CartMainText>
                </S.Texts>
                {note && (
                  <S.Note
                    forwardedAs="span"
                    color="black500"
                    size="text-small"
                    wordBreak="break-word"
                  >
                    {note}
                  </S.Note>
                )}
              </S.ProductRow>
            )
          })}
          {tipAmount > 0 && (
            <S.ProductRow>
              <S.Texts>
                <S.CartMainText forwardedAs="span" color="black800" breakLines={2}>
                  {t('market-shop:orderSummary.tip')}
                </S.CartMainText>
                <S.CartMainText forwardedAs="span" color="black800" whiteSpace="nowrap">
                  {formatPrice(tipAmount)}
                </S.CartMainText>
              </S.Texts>
            </S.ProductRow>
          )}
        </S.ProductsWrapper>

        {orderNote && (
          <S.OrderNoteContainer>
            <Typography as="h3" size="heading-tiny" color="black800">
              {t('market-shop:orderSummary.orderNoteTitle')}
            </Typography>
            <S.Note forwardedAs="span" color="black500" size="text-small" wordBreak="break-word">
              {orderNote}
            </S.Note>
          </S.OrderNoteContainer>
        )}

        <S.PricesContainer>
          <S.Texts>
            <S.CartMainText forwardedAs="span" color="black800" size="text-medium" breakLines={2}>
              {t('market-shop:orderSummary.subTotal')}
            </S.CartMainText>
            <S.CartMainText
              forwardedAs="span"
              color="black800"
              size="text-medium"
              whiteSpace="nowrap"
            >
              {formatPrice(subtotal, { zeroPrice: true })}
            </S.CartMainText>
          </S.Texts>

          {discount > 0 && (
            <S.Texts>
              <S.CartMainText forwardedAs="span" color="black800" size="text-medium" breakLines={2}>
                {t('market-shop:orderSummary.discount')}
              </S.CartMainText>
              <S.CartMainText
                forwardedAs="span"
                color="black800"
                size="text-medium"
                whiteSpace="nowrap"
              >
                -{formatPrice(discount, { zeroPrice: true })}
              </S.CartMainText>
            </S.Texts>
          )}

          <S.Texts>
            <S.CartMainText forwardedAs="span" color="black800" size="text-medium" breakLines={2}>
              {t('market-shop:orderSummary.tax')}
            </S.CartMainText>
            <S.CartMainText
              forwardedAs="span"
              color="black800"
              size="text-medium"
              whiteSpace="nowrap"
            >
              {formatPrice(tax, { zeroPrice: true })}
            </S.CartMainText>
          </S.Texts>

          <S.Texts>
            <Typography
              as="span"
              color="black800"
              size="heading-large"
              weight="bold"
              breakLines={2}
            >
              {t('market-shop:orderSummary.total')}
            </Typography>
            <Typography
              as="span"
              color="black800"
              size="heading-large"
              whiteSpace="nowrap"
              weight="bold"
            >
              {formatPrice(total, { zeroPrice: true })}
            </Typography>
          </S.Texts>
        </S.PricesContainer>
      </S.OrderSummaryModuleContent>
    </Section>
  )
}
